import { type BaseTemplateProps } from '../NotificationTemplate'
import { type TemplateDefinition } from '../TemplateRegistry'

interface ArIpmChangeRequestProps extends BaseTemplateProps {
  request?: {
    name?: string
    metadata?: {
      reviewUrl?: string
      review_url?: string
    }
  }
  ams?: {
    currentGroupKey?: string
    current_group_key?: string
    approvedGroupDisplayName?: string
  }
}

type IpmTemplateKeys = {
  body: string
  partnerBody?: string
  title: string
  ctaLabel: string
}

const currentGroupKey = (props: ArIpmChangeRequestProps): string =>
  props?.ams?.currentGroupKey || props?.ams?.current_group_key || ''

const values = (props: ArIpmChangeRequestProps) => ({
  changeRequestName: props?.request?.name,
  approvedGroupDisplayName: props?.ams?.approvedGroupDisplayName,
})

const reviewUrl = (props: ArIpmChangeRequestProps): string =>
  props?.request?.metadata?.reviewUrl || props?.request?.metadata?.review_url || ''

const defineIpmTemplate = (keys: IpmTemplateKeys): TemplateDefinition<ArIpmChangeRequestProps> => ({
  renderParams: (props: ArIpmChangeRequestProps) => ({
    messageKey:
      keys.partnerBody && currentGroupKey(props) === 'PartnerReview' ? keys.partnerBody : keys.body,
    values: values(props),
  }),
  getTitleParams: (props: ArIpmChangeRequestProps) => ({
    messageKey: keys.title,
    values: values(props),
  }),
  getCTALabelParams: (props: ArIpmChangeRequestProps) =>
    reviewUrl(props)
      ? {
          messageKey: keys.ctaLabel,
        }
      : undefined,
  getLink: (props: ArIpmChangeRequestProps) => {
    const url = reviewUrl(props)
    return keys.ctaLabel === 'notificationsDomain.templates.arIpmChangeRequest.ctaLabel.view'
      ? url.replace(/\/review$/, '')
      : url
  },
})

export const ArIpmChangeRequestCreatedNotifyAuthor = defineIpmTemplate({
  body: 'notificationsDomain.templates.arIpmChangeRequestCreatedNotifyAuthor',
  title: 'notificationsDomain.templates.arIpmChangeRequestCreatedNotifyAuthor.title',
  ctaLabel: 'notificationsDomain.templates.arIpmChangeRequest.ctaLabel.view',
})

export const ArIpmChangeRequestCreatedNotifyApprovers = defineIpmTemplate({
  body: 'notificationsDomain.templates.arIpmChangeRequestCreatedNotifyApprovers',
  partnerBody: 'notificationsDomain.templates.arIpmChangeRequestCreatedNotifyApprovers.partner',
  title: 'notificationsDomain.templates.arIpmChangeRequestCreatedNotifyApprovers.title',
  ctaLabel: 'notificationsDomain.templates.arIpmChangeRequest.ctaLabel.review',
})

export const ArIpmChangeRequestAddedApprovalNotifyApprovers = defineIpmTemplate({
  body: 'notificationsDomain.templates.arIpmChangeRequestAddedApprovalNotifyApprovers',
  title: 'notificationsDomain.templates.arIpmChangeRequestAddedApprovalNotifyApprovers.title',
  ctaLabel: 'notificationsDomain.templates.arIpmChangeRequest.ctaLabel.view',
})

export const ArIpmChangeRequestAddedApprovalNotifyFollowers = defineIpmTemplate({
  body: 'notificationsDomain.templates.arIpmChangeRequestAddedApprovalNotifyFollowers',
  title: 'notificationsDomain.templates.arIpmChangeRequestAddedApprovalNotifyFollowers.title',
  ctaLabel: 'notificationsDomain.templates.arIpmChangeRequest.ctaLabel.view',
})

export const ArIpmChangeRequestAddedPartialApprovalNotifyApprovers = defineIpmTemplate({
  body: 'notificationsDomain.templates.arIpmChangeRequestAddedPartialApprovalNotifyApprovers',
  partnerBody:
    'notificationsDomain.templates.arIpmChangeRequestAddedPartialApprovalNotifyApprovers.partner',
  title:
    'notificationsDomain.templates.arIpmChangeRequestAddedPartialApprovalNotifyApprovers.title',
  ctaLabel: 'notificationsDomain.templates.arIpmChangeRequest.ctaLabel.review',
})

export const ArIpmChangeRequestAddedPartialApprovalNotifyFollowers = defineIpmTemplate({
  body: 'notificationsDomain.templates.arIpmChangeRequestAddedPartialApprovalNotifyFollowers',
  title:
    'notificationsDomain.templates.arIpmChangeRequestAddedPartialApprovalNotifyFollowers.title',
  ctaLabel: 'notificationsDomain.templates.arIpmChangeRequest.ctaLabel.view',
})

export const ArIpmChangeRequestAddedCommentNotifyApprovers = defineIpmTemplate({
  body: 'notificationsDomain.templates.arIpmChangeRequestAddedCommentNotifyApprovers',
  partnerBody:
    'notificationsDomain.templates.arIpmChangeRequestAddedCommentNotifyApprovers.partner',
  title: 'notificationsDomain.templates.arIpmChangeRequestAddedCommentNotifyApprovers.title',
  ctaLabel: 'notificationsDomain.templates.arIpmChangeRequest.ctaLabel.view',
})

export const ArIpmChangeRequestAddedCommentNotifyFollowers = defineIpmTemplate({
  body: 'notificationsDomain.templates.arIpmChangeRequestAddedCommentNotifyFollowers',
  title: 'notificationsDomain.templates.arIpmChangeRequestAddedCommentNotifyFollowers.title',
  ctaLabel: 'notificationsDomain.templates.arIpmChangeRequest.ctaLabel.view',
})

export const ArIpmChangeRequestAddedCancelNotifyApprovers = defineIpmTemplate({
  body: 'notificationsDomain.templates.arIpmChangeRequestAddedCancelNotifyApprovers',
  partnerBody: 'notificationsDomain.templates.arIpmChangeRequestAddedCancelNotifyApprovers.partner',
  title: 'notificationsDomain.templates.arIpmChangeRequestAddedCancelNotifyApprovers.title',
  ctaLabel: 'notificationsDomain.templates.arIpmChangeRequest.ctaLabel.view',
})

export const ArIpmChangeRequestAddedCancelNotifyFollowers = defineIpmTemplate({
  body: 'notificationsDomain.templates.arIpmChangeRequestAddedCancelNotifyFollowers',
  title: 'notificationsDomain.templates.arIpmChangeRequestAddedCancelNotifyFollowers.title',
  ctaLabel: 'notificationsDomain.templates.arIpmChangeRequest.ctaLabel.view',
})

export const ArIpmChangeRequestPublishedNotifyApprovers = defineIpmTemplate({
  body: 'notificationsDomain.templates.arIpmChangeRequestPublishedNotifyApprovers',
  partnerBody: 'notificationsDomain.templates.arIpmChangeRequestPublishedNotifyApprovers.partner',
  title: 'notificationsDomain.templates.arIpmChangeRequestPublishedNotifyApprovers.title',
  ctaLabel: 'notificationsDomain.templates.arIpmChangeRequest.ctaLabel.view',
})

export const ArIpmChangeRequestPublishedNotifyFollowers = defineIpmTemplate({
  body: 'notificationsDomain.templates.arIpmChangeRequestPublishedNotifyFollowers',
  title: 'notificationsDomain.templates.arIpmChangeRequestPublishedNotifyFollowers.title',
  ctaLabel: 'notificationsDomain.templates.arIpmChangeRequest.ctaLabel.view',
})
